import { createContext, useContext, useState, type ReactNode } from 'react';

/**
 * Basic mode keeps the console uncluttered for everyday operation; advanced mode exposes
 * the deeper routing and tunnel machinery (route explorer, areas, timers, policy tables).
 * The choice is a per-browser preference, persisted so it survives a reload, and read
 * through {@link useMode} / {@link AdvancedOnly} rather than prop-drilled.
 */
export type UiMode = 'basic' | 'advanced';

interface PreferencesValue {
  mode: UiMode;
  setMode: (mode: UiMode) => void;
  toggleMode: () => void;
}

const STORAGE_KEY = 'connaro.mode';
const PreferencesContext = createContext<PreferencesValue | null>(null);

function readStoredMode(): UiMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'advanced' ? 'advanced' : 'basic';
  } catch {
    return 'basic';
  }
}

export function PreferencesProvider({ children }: { children: ReactNode }): JSX.Element {
  const [mode, setModeState] = useState<UiMode>(readStoredMode);

  const setMode = (next: UiMode): void => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage may be unavailable (private mode); the preference just won't persist.
    }
  };

  const toggleMode = (): void => setMode(mode === 'advanced' ? 'basic' : 'advanced');

  return (
    <PreferencesContext.Provider value={{ mode, setMode, toggleMode }}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within a PreferencesProvider');
  return ctx;
}

/** The current UI mode. */
export function useMode(): UiMode {
  return usePreferences().mode;
}
